import { SetStateAction, useMemo, useState } from 'react';
import {
  EntityReviewStatus,
  EntityValidationStatus,
  OperationalModel,
  ReadableEntityReviewStatus,
  ReadableEntityValidationStatus,
} from '@constants/canvas/layers';
import { filtersSubject } from '@context/ProjectFilters/FiltersSubject';
import { CiaFilters } from '@context/ProjectFilters/types';

const validationStatusOptions = [
  EntityValidationStatus.Validated,
  EntityValidationStatus.Draft,
].map((status) => ({
  label: ReadableEntityValidationStatus[status],
  value: status,
}));

const reviewStatusOptions = [EntityReviewStatus.ReviewRequired].map(
  (status) => ({
    label: ReadableEntityReviewStatus[status],
    value: status,
  }),
);

export const useFilters = () => {
  const [validationStatus, setValidationStatus] = useState([]);
  const [reviewStatus, setReviewStatus] = useState([]);
  const [operationalStatuses, setOperationalStatuses] = useState([]);
  const [operatingModelStatuses, setOperatingModelStatuses] = useState<
    OperationalModel[]
  >([]);

  const [partners, setPartners] = useState<string[]>([]);

  const [ciaFilters, setCiaFilters] = useState<CiaFilters>({
    assurance_level: {},
    coverage: {},
    applicability: {},
  });

  const ciaSelectedFilters = useMemo(
    () =>
      Object.values(ciaFilters).reduce<string[]>((selected, fields) => {
        return [...selected, ...(Object.values(fields).flat() as string[])];
      }, []),
    [ciaFilters],
  );

  const selectedFilters = useMemo(
    () => [
      ...validationStatus,
      ...reviewStatus,
      ...operationalStatuses,
      ...operatingModelStatuses,
      ...ciaSelectedFilters,
      ...partners,
    ],
    [
      validationStatus,
      reviewStatus,
      operationalStatuses,
      operatingModelStatuses,
      ciaSelectedFilters,
      partners,
    ],
  );

  const handleSetValidationStatus = (validationStatus: string[]) => {
    setValidationStatus(validationStatus as never[]);
    filtersSubject.next({ ...filtersSubject.value, validationStatus });
  };

  const handleSetReviewStatus = (reviewStatus: string[]) => {
    setReviewStatus(reviewStatus as never[]);
    filtersSubject.next({ ...filtersSubject.value, reviewStatus });
  };

  const handleSetOperationalStatus = (statuses: string[]) => {
    setOperationalStatuses(statuses as never[]);
    filtersSubject.next({
      ...filtersSubject.value,
      operationalStatuses: statuses,
    });
  };

  const handleSetOperatingModelStatus = (statuses: OperationalModel[]) => {
    setOperatingModelStatuses(statuses);

    filtersSubject.next({
      ...filtersSubject.value,
      operatingModelStatuses: statuses,
    });
  };

  const handleSetPartners = (partners: string[]) => {
    setPartners(partners);
    filtersSubject.next({ ...filtersSubject.value, partners });
  };

  const handleSetCiaFilters = (newState: SetStateAction<CiaFilters>) => {
    setCiaFilters(newState);

    const cia =
      typeof newState === 'function' ? newState(ciaFilters) : newState;

    filtersSubject.next({ ...filtersSubject.value, cia });
  };

  const handleClearAllFilters = () => {
    handleSetValidationStatus([]);

    handleSetReviewStatus([]);

    handleSetCiaFilters({
      assurance_level: {},
      coverage: {},
      applicability: {},
    });

    handleSetOperationalStatus([]);
    handleSetOperatingModelStatus([]);
    handleSetPartners([]);
  };

  return {
    validationStatusOptions,
    reviewStatusOptions,
    ciaFilters,
    validationStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    reviewStatus,
    setValidationStatus: handleSetValidationStatus,
    setReviewStatus: handleSetReviewStatus,
    setCiaFilters: handleSetCiaFilters,
    setOperationalStatuses: handleSetOperationalStatus,
    setOperatingModelStatuses: handleSetOperatingModelStatus,
    setPartners: handleSetPartners,
    selectedFilters,
    handleClearAllFilters,
  };
};
